import { useEffect, useState } from "react";
import { Crown, ShieldCheck, Rocket, Building2, ArrowRight, Loader2, UserPlus, UserMinus } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * iter79 — Platform Roles tab.
 *
 * Single source of truth for platform-tier role management:
 *   - Lists all site_admins grouped by tier (Admin / Developer / Tenant Admin).
 *   - Lets platform_admin grant/revoke the `platform_developer` role.
 *
 * Lives in the Platform Admin scope ONLY. Site Admin scope no longer
 * mutates platform-tier roles — its Site Admins tab is read-only for
 * role badges and focused on tenant-level operations (invite/delete).
 */

const ROLE_META = {
    platform_admin: {
        label: "Commons Admin",
        Icon: Crown,
        color: "text-cyber",
        border: "border-cyber/40",
        bg: "bg-cyber/5",
        description: "Full unrestricted power across all tenants. Manages release notes, tenant lifecycle, docs, platform passcode, and grants/revokes the Commons Ops role.",
        meta: "Env-seed only (`SITE_ADMIN_EMAIL` + `SITE_ADMIN_PASSWORD`). Cannot be granted via the API.",
    },
    platform_developer: {
        label: "Commons Ops",
        Icon: Rocket,
        color: "text-volt",
        border: "border-volt/40",
        bg: "bg-volt/5",
        description: "Scoped to the Release Ops tab + their own Account. Sees the Deployment Readiness banner, the Runbook Gate, and (coming soon) GitHub / Deploy status. No tenant data access.",
        meta: 'Grantable by any Commons Admin below — "Promote to Developer" button on any Tenant Admin row.',
    },
    tenant_admin: {
        label: "Tenant Admin",
        Icon: Building2,
        color: "text-zinc-400",
        border: "border-[#333]",
        bg: "",
        description: "Per-tenant operational scope: manages their tenant's leagues, tournaments, participants, subscriptions, branding, and league-admin permissions. Default for every invited site admin.",
        meta: "Auto-assigned on invite. Revert a Commons Ops to this tier from the table below.",
    },
};

function RoleReferenceCard({ role }) {
    const meta = ROLE_META[role];
    const { Icon } = meta;
    return (
        <div className={`brutal-card p-4 border-2 ${meta.border} ${meta.bg}`} data-testid={`role-card-${role.replace("_", "-")}`}>
            <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-5 h-5 ${meta.color}`} />
                <h3 className="font-display font-extrabold uppercase text-sm">{meta.label}</h3>
            </div>
            <p className="text-xs text-zinc-300 leading-relaxed">{meta.description}</p>
            <p className="text-[10px] text-zinc-500 mt-3 italic">{meta.meta}</p>
        </div>
    );
}

function resolveRole(sa) {
    return sa.role || (sa.is_super ? "platform_admin" : "tenant_admin");
}

export default function PlatformAdminsPlaceholderTab() {
    const [siteAdmins, setSiteAdmins] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [meId, setMeId] = useState(null);

    const reload = async () => {
        try {
            const [list, me] = await Promise.all([
                api.authListSiteAdmins(),
                api.authMe(),
            ]);
            setSiteAdmins(list);
            setMeId(me?.id);
        } catch (err) {
            const msg = err.response?.data?.detail || "Failed to load platform roles";
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { reload(); }, []);

    const setRole = async (sa, newRole) => {
        const verb = newRole === "platform_developer" ? "Promote to Commons Ops" : "Revoke Commons Ops";
        if (!window.confirm(`${verb}: ${sa.email}?`)) return;
        setBusyId(sa.id);
        try {
            await api.setSiteAdminRole(sa.id, newRole);
            toast.success(newRole === "platform_developer" ? `${sa.email} is now Commons Ops` : `Reverted ${sa.email} to Tenant Admin`);
            await reload();
        } catch (err) {
            const msg = err.response?.data?.detail;
            toast.error(typeof msg === "string" ? msg : "Failed");
        } finally {
            setBusyId(null);
        }
    };

    const grouped = (siteAdmins || []).reduce((acc, sa) => {
        const r = resolveRole(sa);
        (acc[r] = acc[r] || []).push(sa);
        return acc;
    }, {});

    const platformAdmins = grouped.platform_admin || [];
    const developers = grouped.platform_developer || [];
    const tenantAdmins = grouped.tenant_admin || [];

    return (
        <div className="space-y-5" data-testid="platform-admins-placeholder-tab">
            <div>
                <h2 className="font-display font-extrabold text-2xl uppercase flex items-center gap-2">
                    <Crown className="w-6 h-6 text-volt" />
                    Platform roles
                </h2>
                <p className="text-xs text-zinc-500 mt-1">
                    Source of truth for platform-tier role assignment. Tenant-level role management lives on the Site Admins tab in Site Admin scope.
                </p>
            </div>

            {/* Role reference */}
            <div className="grid sm:grid-cols-1 lg:grid-cols-3 gap-3">
                <RoleReferenceCard role="platform_admin" />
                <RoleReferenceCard role="platform_developer" />
                <RoleReferenceCard role="tenant_admin" />
            </div>

            {loading && (
                <div className="brutal-card p-6 text-center" data-testid="platform-roles-loading">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-500" />
                    <p className="text-xs text-zinc-500 mt-2">Loading members…</p>
                </div>
            )}

            {!loading && (
                <>
                    {/* Platform Admins — read-only */}
                    <div className="brutal-card p-4" data-testid="platform-admins-list">
                        <div className="flex items-center gap-2 mb-3">
                            <Crown className="w-4 h-4 text-cyber" />
                            <h3 className="font-display font-extrabold uppercase text-sm">Commons Admins · {platformAdmins.length}</h3>
                            <span className="pill text-[9px] font-bold uppercase border bg-zinc-500/15 text-zinc-400 border-zinc-500/40">Read-only · env-seed</span>
                        </div>
                        {platformAdmins.length === 0 ? (
                            <p className="text-xs text-zinc-500 italic">None — your env seed didn't run.</p>
                        ) : (
                            <ul className="space-y-1.5">
                                {platformAdmins.map((sa) => (
                                    <li key={sa.id} className="flex items-center justify-between text-xs py-1.5 border-b border-[#1a1a1a] last:border-b-0">
                                        <div>
                                            <span className="font-bold text-cyber">{sa.email}</span>
                                            {sa.id === meId && <span className="text-[10px] text-volt ml-2">(you)</span>}
                                            <span className="text-zinc-500 ml-2">· {sa.name || "—"}</span>
                                        </div>
                                        <span className="text-[10px] text-zinc-600">since {new Date(sa.created_at).toLocaleDateString()}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Platform Developers — interactive */}
                    <div className="brutal-card p-4" data-testid="platform-developers-list">
                        <div className="flex items-center gap-2 mb-3">
                            <Rocket className="w-4 h-4 text-volt" />
                            <h3 className="font-display font-extrabold uppercase text-sm">Commons Ops · {developers.length}</h3>
                            <span className="pill text-[9px] font-bold uppercase border bg-volt/15 text-volt border-volt/40">Revocable</span>
                        </div>
                        {developers.length === 0 ? (
                            <p className="text-xs text-zinc-500 italic">No developers yet. Promote a Tenant Admin below.</p>
                        ) : (
                            <ul className="space-y-1.5">
                                {developers.map((sa) => (
                                    <li key={sa.id} data-testid={`developer-row-${sa.id}`} className="flex items-center justify-between text-xs py-1.5 border-b border-[#1a1a1a] last:border-b-0">
                                        <div className="min-w-0 flex-1">
                                            <span className="font-bold text-volt">{sa.email}</span>
                                            <span className="text-zinc-500 ml-2">· {sa.name || "—"}</span>
                                            <span className="text-[10px] text-zinc-600 ml-2">since {new Date(sa.created_at).toLocaleDateString()}</span>
                                        </div>
                                        <button
                                            data-testid={`revoke-developer-${sa.id}-btn`}
                                            disabled={busyId === sa.id}
                                            onClick={() => setRole(sa, "tenant_admin")}
                                            className="brutal-btn-ghost text-[10px] py-1.5 px-2 disabled:opacity-50"
                                        >
                                            <UserMinus className="w-3 h-3" />
                                            {busyId === sa.id ? "…" : "Revoke"}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Tenant Admins — promotable */}
                    <div className="brutal-card p-4" data-testid="tenant-admins-promote-list">
                        <div className="flex items-center gap-2 mb-3">
                            <Building2 className="w-4 h-4 text-zinc-400" />
                            <h3 className="font-display font-extrabold uppercase text-sm">Tenant Admins · {tenantAdmins.length}</h3>
                            <span className="pill text-[9px] font-bold uppercase border bg-zinc-500/15 text-zinc-400 border-zinc-500/40">Promotable</span>
                        </div>
                        {tenantAdmins.length === 0 ? (
                            <p className="text-xs text-zinc-500 italic">No Tenant Admins to promote. Invite one from the Site Admin scope first.</p>
                        ) : (
                            <ul className="space-y-1.5">
                                {tenantAdmins.map((sa) => (
                                    <li key={sa.id} data-testid={`tenant-admin-row-${sa.id}`} className="flex items-center justify-between text-xs py-1.5 border-b border-[#1a1a1a] last:border-b-0">
                                        <div className="min-w-0 flex-1">
                                            <span className="font-bold text-zinc-300">{sa.email}</span>
                                            <span className="text-zinc-500 ml-2">· {sa.name || "—"}</span>
                                            <span className="text-[10px] text-zinc-600 ml-2">since {new Date(sa.created_at).toLocaleDateString()}</span>
                                        </div>
                                        <button
                                            data-testid={`promote-developer-${sa.id}-btn`}
                                            disabled={busyId === sa.id}
                                            onClick={() => setRole(sa, "platform_developer")}
                                            className="brutal-btn text-[10px] py-1.5 px-2 disabled:opacity-50"
                                        >
                                            <UserPlus className="w-3 h-3" />
                                            {busyId === sa.id ? "…" : "Promote to Developer"}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </>
            )}

            {/* Pointer to Site Admins scope for invites */}
            <div className="brutal-card p-4 border-2 border-[#222] bg-[#0a0a0a]" data-testid="role-mgmt-pointer">
                <div className="flex items-center gap-3">
                    <ShieldCheck className="w-5 h-5 text-cyber shrink-0" />
                    <div className="flex-1">
                        <p className="text-xs text-zinc-300 leading-relaxed">
                            <span className="font-bold">Need to invite a new Site Admin first?</span> Switch to
                            <span className="font-bold text-cyber"> Site Admin </span>
                            scope → Site Admins tab → use the invite form. They'll arrive here as Tenant Admins and you can promote them to Developer above.
                        </p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-zinc-500 shrink-0" />
                </div>
            </div>
        </div>
    );
}
